import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FlowCard } from "../components/FlowCard";
import { useLeadModal } from "../components/LeadModal";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { openModal } = useLeadModal();

  return (
    <section id="inicio" className="bg-ice relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-44">
      <div className="glow-brand pointer-events-none absolute -left-48 top-24 h-[520px] w-[520px] rounded-full" />
      <div className="glow-brand pointer-events-none absolute -right-48 bottom-0 h-[460px] w-[460px] rounded-full" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-brand/20 bg-brand/5 px-4 py-1.5 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-brand"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
            </span>
            Gestão de demandas imobiliárias
          </motion.span>

          <h1
            className="mt-7 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-petrol sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
              >
                Problemas no imóvel?
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-4">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
              >
                <span className="relative inline-block text-brand">
                  A gente resolve.
                  <motion.svg
                    viewBox="0 0 320 14"
                    className="absolute -bottom-2 left-0 w-full"
                    fill="none"
                    aria-hidden="true"
                  >
                    <motion.path
                      d="M5 10 C 90 2, 230 2, 315 8"
                      stroke="#FF5C00"
                      strokeWidth="5"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 1.05, duration: 0.8, ease: "easeOut" }}
                    />
                  </motion.svg>
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subheadline"
          >
            A Resolv simplifica a gestão de demandas imobiliárias, conectando clientes,
            imobiliárias e profissionais para que cada problema tenha uma solução rápida,
            organizada e acompanhada do início ao fim.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openModal()}
              data-testid="hero-cta-request"
              className="group inline-flex h-[52px] items-center gap-2 rounded-full bg-brand px-8 text-[0.95rem] font-semibold text-white shadow-[0_16px_40px_-10px_rgba(0,102,255,0.55)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-[0_20px_44px_-10px_rgba(0,102,255,0.6)]"
            >
              Solicitar atendimento
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#como-funciona")}
              data-testid="hero-cta-discover"
              className="inline-flex h-[52px] items-center rounded-full border border-slate-300 bg-white/60 px-8 text-[0.95rem] font-semibold text-petrol backdrop-blur transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-brand hover:text-brand"
            >
              Conheça a Resolv
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[0.65rem] font-medium uppercase tracking-[0.18em] text-slate-500"
            data-testid="hero-trust-row"
          >
            <span>Rede de profissionais qualificados</span>
            <span className="h-1 w-1 rounded-full bg-brand" />
            <span>Acompanhamento ponta a ponta</span>
          </motion.div>
        </div>

        <FlowCard />
      </div>
    </section>
  );
};
